/**
 * Debug wiring (?debug=1) URL params (plan 03 Phase 1): probe snapshot, pause hook, godmode.
 *
 * Pure reader over URLSearchParams — main.ts keeps the `new URLSearchParams(location.search)`
 * call at the boundary (same shape as readBootLevelIndex), so the gating is unit-testable without
 * executing main() (which runs the whole boot at module load).
 */

/** Debug-only (godmode): the crowd the boss-phase shots measure — the High soldier cap. */
export const GODMODE_BOSS_N_FLOOR = 48;

/** Debug wiring (?debug=1): probe snapshot, pause hook, godmode (plan 03 Phase 1). */
export interface DebugHooks {
  enabled: boolean;
  godmode: boolean;
  /** Sim-time multiplier for headless replays (pause shots stay frame-exact). */
  simSpeed: number;
  pauseAtZ: number | null;
  paused: boolean;
  /** Rolling max of live bullets, updated per SIM TICK (not wall-clock) so the latch holds
   * through the pause (opus r3 N2) and never false-reds on frames between bullet deaths. */
  bulletCountMax: number;
  /**
   * Plan 05 P5.6 measurement lever: `?noshadow=1` forces the High-tier shadow cascade off so
   * tools/inspect.mjs can diff the frame counters against the shadowed run (`shadowTriOverlap`).
   * Debug-only by construction — it is read next to the other debug hooks and honoured only when
   * `?debug=1` is present too; the tier itself stays fixed at boot (plan 05 Design 7), so the
   * shadow path never toggles shaders mid-run on a normal page.
   */
  noShadow: boolean;
  probe: unknown | null;
}

/**
 * Audit #5: godmode/simSpeed/pauseAtZ used to be read WITHOUT `?debug=1`, and none of them is
 * inert — the pause gate in the render loop is not debug-gated while its only resume hook
 * (`__jreb1dResume`) registers solely under debug, so `?pauseAtZ=100` alone froze the live page
 * forever; `?godmode=1` alone was invincibility and `?simSpeed=16` alone ran 16× past the
 * MAX_STEPS_PER_FRAME cap. Without `debug=1` every knob now reads its inert default: godmode
 * false, simSpeed 1, pauseAtZ null. noShadow joins them for consistency — its only consumer
 * already ANDs with `enabled`, so its behaviour is unchanged.
 */
export function readDebugHooks(params: URLSearchParams): DebugHooks {
  const enabled = params.get('debug') === '1';
  const godmode = enabled && params.get('godmode') === '1';
  const pauseRaw = enabled ? params.get('pauseAtZ') : null;
  // `?pauseAtZ=` with no (or whitespace-only) value must NOT park the sim at z=0 (Number('') ===
  // 0): a blank value means "no pause" (plan 16 To-fix finding 11).
  const pauseAtZ = pauseRaw !== null && pauseRaw.trim() !== '' && Number.isFinite(Number(pauseRaw)) ? Number(pauseRaw) : null;
  const speedRaw = enabled ? Number(params.get('simSpeed')) : NaN;
  const simSpeed = Number.isFinite(speedRaw) && speedRaw >= 1 && speedRaw <= 16 ? speedRaw : 1;
  const noShadow = enabled && params.get('noshadow') === '1';
  return { enabled, godmode, simSpeed, pauseAtZ, paused: false, bulletCountMax: 0, noShadow, probe: null };
}
